import type { ApplicationContext, SpriteImageSource } from './styleApplicator.types';
import type { CSSObject, Sprite } from './themeOutput.types';

/**
 * Theme art as `<img>` (PRNT-4898 follow-up).
 *
 * The published sprite is a CSS `background-image`, which the browser only discovers after CSSOM
 * and layout — never from the preload scanner. An `<img>` in the SSR markup is discovered while
 * the HTML streams, and can be told to load eagerly rather than on approach.
 * These helpers derive the `<img>` attributes and the matching art-layer style from
 * the sprite JSON; the server-side preload links are built from the same functions so a
 * preload always resolves to the URL the image itself will pick (a mismatch downloads twice).
 *
 * Deliberately NO fetchpriority either, on either consumer. The header photo is the LCP element
 * and keeps the high band to itself; theme art is decoration that merely needs to start early,
 * which `<img>` discovery and the `<head>` preload already give it. Marking art high put a
 * 5120x2880 sprite in front of the hero on the worst themes. Both sides must stay silent on
 * priority together — one side alone leaves the browser free to promote the request anyway.
 *
 * Deliberately NO srcset. `@Nx` names a pre-rendered file, so a rendition ladder is expressible
 * (@3x is the master, @2x/@1x its exact 2/3 and 1/3 downscales) — but only the URL the published
 * JSON carries has ever been requested, so it is the only one warm at the CDN edges. Measured on
 * a cold edge, a sibling rendition takes 0.9-1.2s to generate, and while it is generating the
 * origin can answer with a body Chrome's Opaque Response Blocking rejects outright
 * (ERR_BLOCKED_BY_ORB, naturalWidth 0 - the art never paints). Both failure modes are exactly the
 * "art arrives after the hero" this change exists to remove, so the ladder is not worth it:
 * serving the same warm URL the CSS background used keeps the win (preload-scanner discovery)
 * without introducing cold URLs. Right-sizing is a separate exercise that needs the renditions
 * warmed at the origin first.
 */

const IMAGE_LAYER = '& > *:first-child';
const BACKGROUND_PROPERTIES = new Set([
  'backgroundImage',
  'backgroundSize',
  'backgroundRepeat',
  'backgroundPosition',
  'backgroundPositionX',
  'backgroundPositionY',
]) as ReadonlySet<string>;

/**
 * The single source of truth for a sprite's image URL, shared by the `<img>` and the server's
 * `<link rel="preload">` so the two can never disagree — a preload that resolves to a different
 * URL than the image picks downloads the art twice.
 */
export const buildSpriteImageSource = (sprite: Sprite): SpriteImageSource => ({ src: sprite.asset.url });

/**
 * Rewrites the sprite's art-layer block for an `<img>`: every published sprite is `no-repeat`
 * with `contain` or `cover` and a keyword position, which is exactly `object-fit` +
 * `object-position`. Input is never mutated — the ThemeOutput it comes from is shared through the
 * fetch cache.
 */
export const toImageLayerStyle = (style: CSSObject): CSSObject => {
  const layer = style[IMAGE_LAYER];
  if (!layer || typeof layer !== 'object') {
    return style;
  }

  const { backgroundSize, backgroundPositionX, backgroundPositionY } = layer as CSSObject;
  const rewritten: CSSObject = {
    objectFit: backgroundSize === 'cover' ? 'cover' : 'contain',
    objectPosition: `${backgroundPositionX ?? 'center'} ${backgroundPositionY ?? 'center'}`,
  };
  Object.entries(layer).forEach(([property, value]) => {
    if (!BACKGROUND_PROPERTIES.has(property)) {
      rewritten[property] = value;
    }
  });

  return { ...style, [IMAGE_LAYER]: rewritten };
};

/**
 * Whether a sprite paints in the first viewport, decided from the JSON alone so the server
 * (which art to preload) and the client (`loading` / `decoding`) always agree. Viewport-fixed art
 * is always on screen; the title banner is the page top; top-anchored frame art and
 * frame-filling centred art start at the top of a frame that begins at the top of the page.
 * Only bottom-anchored (and non-filling centred) art is left to lazy-load.
 */
export const isAboveTheFold = (sprite: Sprite, context: ApplicationContext): boolean => {
  if (sprite.fixedPositionWhenScrolling || 'component' in context) {
    return true;
  }
  if (sprite.placement.y === 'top') {
    return true;
  }
  return sprite.placement.y === 'center' && sprite.scale.constraint === 'parent-fill';
};
